import { useState, useEffect, useRef } from 'react';
import { Star, X, Loader2 } from 'lucide-react';
import { useFeedback } from '../../hooks/useFeedback';
import { hasFeedbackCooldown, setFeedbackCooldown } from '../../lib/feedback';

const MAX_COMMENT = 280;

interface StarRatingProps {
  featureKey: string;
  label?: string;
  onDismiss?: () => void;
  onSubmitted?: () => void;
}

export function StarRating({ featureKey, label, onDismiss, onSubmitted }: StarRatingProps) {
  const [visible, setVisible] = useState(false);
  const [hovered, setHovered] = useState(0);
  const [selected, setSelected] = useState(0);
  const [comment, setComment] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [thanks, setThanks] = useState(false);
  const { submit, isSubmitting, error } = useFeedback();
  const thanksTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setVisible(!hasFeedbackCooldown(featureKey));
  }, [featureKey]);

  useEffect(() => {
    return () => {
      if (thanksTimerRef.current) clearTimeout(thanksTimerRef.current);
    };
  }, []);

  if (!visible || submitted) return null;

  const handleDismiss = () => {
    setFeedbackCooldown(featureKey);
    setVisible(false);
    onDismiss?.();
  };

  const handleStarClick = (rating: number) => {
    setSelected(rating);
  };

  const handleSubmit = async () => {
    if (!selected) return;
    try {
      await submit({
        feedbackType: 'feature_rating',
        featureRating: {
          featureKey,
          rating: selected as 1 | 2 | 3 | 4 | 5,
          ...(comment.trim() ? { comment: comment.trim() } : {}),
        },
      });
      setFeedbackCooldown(featureKey);
      setThanks(true);
      thanksTimerRef.current = setTimeout(() => {
        setSubmitted(true);
        onSubmitted?.();
      }, 1500);
    } catch {
      // error surfaced via useFeedback().error
    }
  };

  if (thanks) {
    return (
      <div className="flex items-center gap-2 py-1.5">
        <span className="text-micro text-emerald-600 dark:text-emerald-400">Thanks!</span>
      </div>
    );
  }

  const displayRating = hovered || selected;

  return (
    <div className="flex flex-col gap-2 py-1.5">
      <div className="flex items-center gap-2">
        {label && (
          <span className="text-micro text-gray-400 dark:text-gray-500">{label}</span>
        )}
        <div className="flex items-center gap-0.5">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => handleStarClick(n)}
              onMouseEnter={() => setHovered(n)}
              onMouseLeave={() => setHovered(0)}
              disabled={isSubmitting}
              className="p-0.5 rounded transition-colors disabled:opacity-50"
              aria-label={`Rate ${n} star${n !== 1 ? 's' : ''}`}
            >
              <Star
                className={`h-4 w-4 transition-colors ${
                  n <= displayRating
                    ? 'fill-current text-amber-400'
                    : 'text-gray-300 dark:text-gray-600'
                }`}
              />
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          className="ml-auto p-0.5 rounded text-gray-300 dark:text-gray-600 hover:text-gray-500 dark:hover:text-gray-400 transition-colors"
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {selected > 0 && (
        <div className="flex flex-col gap-1.5">
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value.slice(0, MAX_COMMENT))}
            placeholder="Optional comment…"
            rows={2}
            maxLength={MAX_COMMENT}
            disabled={isSubmitting}
            className="w-full px-2.5 py-1.5 rounded-lg bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-micro text-gray-700 dark:text-gray-300 placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30 resize-none disabled:opacity-60"
          />
          <div className="flex items-center justify-between gap-2">
            {error && (
              <span className="text-micro text-red-500 dark:text-red-400">{error}</span>
            )}
            {!error && (
              <span className="text-micro text-gray-400 dark:text-gray-600">
                {comment.length}/{MAX_COMMENT}
              </span>
            )}
            <button
              type="button"
              onClick={() => void handleSubmit()}
              disabled={isSubmitting}
              className="flex items-center gap-1 px-3 py-1 bg-primary text-white rounded-lg text-micro font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 ml-auto"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Sending…
                </>
              ) : (
                'Submit'
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
